import {
  getGasFutureProductsChart,
  getGasFutureProductsDailyIndicators,
} from "$/api/market-monitoring/gas.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import { GraphIndicators } from "$/lib/components/graphs/GraphIndicator/GraphIndicators";
import LineGraph from "$/lib/components/graphs/LineGraph";
import { Tabs } from "$/lib/components/molecules";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import { CheckboxSet } from "$/lib/components/molecules/ChecboxSet";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import type { FutureProductPeriod } from "$/lib/types/models/FutureProduct.types";
import { DEFAULT_GRAPH_TABS } from "$/lib/utils/constants/marketMonitoring/graphs.constants";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import {
  DEFAULT_GRAPH_COLORS,
  handleOnChangeDefaultGraphsTabs,
  PEG_GAS_COLORS,
  TTF_GAS_COLORS,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const COLORS = [
  "#2e6d38",
  "#67a158",
  "#b3c365",
  "#aa154a",
  "#e56453",
  "#f09883",
  ...DEFAULT_GRAPH_COLORS,
];

type Props = {
  title: string;
  period: FutureProductPeriod;
  selectedDate: Date;
} & CustomDashboardProps;

const GasFutureProductsChart = ({
  title,
  period,
  selectedDate: defaultSelectedDate,
  id,
  isDraggable = false,
  isShowcase = false,
}: Props) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const [selectedDate, setSelectedDate] = useState<Date>(defaultSelectedDate);

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [filters, setFilters] = useState<{
    PEG: boolean;
    TTF: boolean;
  }>({
    PEG: true,
    TTF: false,
  });

  const [indices, setIndices] = useState<
    | {
        start: number;
        end: number;
      }
    | undefined
  >();

  const { data, isLoading } = useQuery({
    queryKey: ["get-gas-future-prod-chart", dateRange, period],
    queryFn: () =>
      getGasFutureProductsChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        period,
      }),
  });

  const { data: daily } = useQuery({
    queryKey: ["get-gas-future-prod-chart", selectedDate, period],
    queryFn: () =>
      getGasFutureProductsDailyIndicators({
        date: getDate(selectedDate),
        period,
      }),
  });

  const toDisplay = useMemo(
    () =>
      data
        ? filters.PEG && filters.TTF
          ? data.mergedData
          : filters.PEG
            ? data.pegData
            : filters.TTF
              ? data.ttfData
              : []
        : [],
    [data, filters],
  );
  const toDisplayVolatility = useMemo(
    () =>
      data
        ? filters.PEG && filters.TTF
          ? data.mergedVolatilityData
          : filters.PEG
            ? data.pegVolatilityData
            : filters.TTF
              ? data.ttfVolatilityData
              : []
        : [],
    [data, filters],
  );

  const pegKeys = useMemo(() => getArrayKeys(data?.pegData ?? []), [data]);
  const ttfKeys = useMemo(() => getArrayKeys(data?.ttfData ?? []), [data]);

  const keys = useMemo(
    () =>
      filters.PEG && filters.TTF
        ? [...pegKeys, ...ttfKeys]
        : filters.PEG
          ? pegKeys
          : filters.TTF
            ? ttfKeys
            : [],
    [filters, pegKeys, ttfKeys],
  );

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const pegColors = useMemo(
    () => [...PEG_GAS_COLORS, ...DEFAULT_GRAPH_COLORS].slice(0, pegKeys.length),
    [pegKeys],
  );
  const ttfColors = useMemo(
    () => [...TTF_GAS_COLORS, ...DEFAULT_GRAPH_COLORS].slice(0, ttfKeys.length),
    [ttfKeys],
  );

  const colors: Record<string, string> = useMemo(
    () =>
      keys.reduce(
        (acc, key, i) => ({
          ...acc,
          [key]:
            filters.PEG && filters.TTF
              ? [...pegColors, ...ttfColors][i]
              : filters.PEG
                ? pegColors[i]
                : filters.TTF
                  ? ttfColors[i]
                  : COLORS[i],
        }),
        {},
      ),
    [filters, keys, pegColors, ttfColors],
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key, i) => ({
        name: key,
        isVisible: true,
        color:
          filters.PEG && filters.TTF
            ? [...pegColors, ...ttfColors][i]
            : filters.PEG
              ? pegColors[i]
              : filters.TTF
                ? ttfColors[i]
                : COLORS[i],
      })),
    );
  }, [filters, keys, pegColors, ttfColors]);

  useEffect(() => {
    setIndices(undefined);
  }, [dateRange]);

  return (
    <DisplayBox
      title={title}
      Icon={LineChartIcon}
      theme="green"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      MiddleComponent={
        <Tabs
          className="mx-auto"
          type="bordered"
          tabs={DEFAULT_GRAPH_TABS}
          activeTab={selectedGraphTabId}
          handleTabChange={setSelectedGraphTabId}
          theme="green"
        />
      }
      id={id}
      isDraggable={isDraggable}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
    >
      <ChartsContainer>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight className="gap-2 rounded-3xl">
            <Flexbox fullWidth className="h-[65%]">
              <LineGraph
                data={toDisplay}
                graphVisibility={graphVisibility}
                title="Prix Stlt EEX"
                xAxisDataKey="date"
                syncId={`id-gas-${period}`}
                colors={colors}
                theme={"green"}
                keys={keys}
                heightPercent={65}
                leftMargin={-10}
                indices={indices}
              />
            </Flexbox>

            <Flexbox fullWidth className="h-[35%]">
              <LineGraph
                data={toDisplayVolatility}
                title="Volatilité"
                graphVisibility={graphVisibility}
                syncId={`id-gas-${period}`}
                withBrush
                xAxisDataKey="date"
                colors={colors}
                theme="green"
                keys={keys}
                heightPercent={40}
                yAxisTicksNumber={7}
                leftMargin={-10}
                setIndices={setIndices}
                indices={indices}
              />
            </Flexbox>
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="green"
          />

          <CheckboxSet<"PEG" | "TTF">
            id={`peg-ttf-${period}`}
            title="Marché"
            selected={filters}
            onChange={(id, value) =>
              setFilters((prev) => {
                const newValue = { ...prev, [id]: value };
                if (!newValue.PEG && !newValue.TTF) return prev;
                return newValue;
              })
            }
            options={[
              { id: "PEG", label: "PEG" },
              { id: "TTF", label: "TTF" },
            ]}
          />

          <Flexbox fullHeight className="gap-4 overflow-y-auto p-1">
            <GraphFilter
              title="Légende"
              data={graphVisibility}
              handleOnChange={handleOnChangeGraphVisiblity}
            />

            {daily && !!daily.length && (
              <Flexbox fullWidth>
                <GraphIndicators
                  selectedDate={selectedDate}
                  data={daily ?? []}
                  indicatorsTitles={{
                    title1: "PEG",
                    title2: "TTF",
                  }}
                />
              </Flexbox>
            )}
          </Flexbox>
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default GasFutureProductsChart;
